import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import styled from "styled-components";
import { FiUpload, FiDownload, FiFilePlus, FiFileText, FiArrowLeft, FiCheckCircle, FiX } from "react-icons/fi";
import { RootState } from "../../store/store";
import { getEvents, IEventItem } from "../../services/eventsApi";
import {
  downloadInventorySample,
  downloadCurrentInventory,
  downloadTransactionReport,
  uploadInventoryExcel,
  updateInventoryExcel,
  InventoryImportSummary,
} from "../../services/inventoryApi";
import Footer from "../Footer/FooterPrice";

const InventoryImportPage: React.FC = () => {
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const [mode, setMode] = useState<"add" | "update">("add");
  const [preview, setPreview] = useState<InventoryImportSummary | null>(null);
  const [hasError, setHasError] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [statusMessage, setStatusMessage] = useState("");
  const [downloading, setDownloading] = useState(false);
  const [transactionEventId, setTransactionEventId] = useState("");
  const [events, setEvents] = useState<IEventItem[]>([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventsError, setEventsError] = useState("");
  const [eventStartDate, setEventStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  });
  const [eventEndDate, setEventEndDate] = useState(() =>
    new Date().toISOString().slice(0, 10)
  );
  const token = useSelector((state: RootState) => state.auth.token);

  useEffect(() => {
    let active = true;
    const loadEvents = async () => {
      setEventsLoading(true);
      setEventsError("");
      try {
        const data = await getEvents();
        if (active) setEvents(data);
      } catch (err: any) {
        if (active) {
          setEventsError(err?.message || "Failed to load events.");
        }
      } finally {
        if (active) setEventsLoading(false);
      }
    };
    loadEvents();
    return () => {
      active = false;
    };
  }, []);

  const filteredEvents = useMemo(() => {
    if (!eventStartDate && !eventEndDate) return events;
    const start = eventStartDate
      ? new Date(`${eventStartDate}T00:00:00`)
      : null;
    const end = eventEndDate
      ? new Date(`${eventEndDate}T23:59:59`)
      : null;
    return events.filter((event) => {
      const startTime = new Date(event.startDateTime);
      if (Number.isNaN(startTime.getTime())) return false;
      if (start && startTime < start) return false;
      if (end && startTime > end) return false;
      return true;
    });
  }, [events, eventStartDate, eventEndDate]);

  const sortedEvents = useMemo(() => {
    return [...filteredEvents].sort(
      (a, b) =>
        new Date(b.startDateTime).getTime() -
        new Date(a.startDateTime).getTime()
    );
  }, [filteredEvents]);

  const formatEventOption = (event: IEventItem) => {
    const start = new Date(event.startDateTime);
    const dateLabel = Number.isNaN(start.getTime())
      ? "Unknown date"
      : start.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        });
    return `${event.title || "Untitled Event"} • ${dateLabel}`;
  };

  const chooseFile = (file?: File) => {
    if (busyRef.current || !file) return;
    setPreview(null);
    setHasError(false);
    if (!/\.(xlsx|xls|csv)$/i.test(file.name) || file.size > 10 * 1024 * 1024) {
      setSelectedFile(null);
      setHasError(true);
      setStatusMessage("Choose an XLSX, XLS, or CSV file up to 10 MB.");
      return;
    }
    setSelectedFile(file);
    setStatusMessage(`Selected: ${file.name}. Review the file before importing.`);
  };
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => chooseFile(e.target.files?.[0]);
  const handleDrop = (e: React.DragEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    chooseFile(e.dataTransfer.files[0]);
  };

  const triggerDownload = (blob: Blob, filename: string) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const bufferLooksLikeXlsx = (buffer: ArrayBuffer) => {
    const bytes = new Uint8Array(buffer.slice(0, 4));
    return bytes[0] === 0x50 && bytes[1] === 0x4b;
  };

  const decodeBase64ToBytes = (text: string) => {
    const cleaned = text.replace(/\s+/g, "");
    const bin = atob(cleaned);
    const out = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i += 1) {
      out[i] = bin.charCodeAt(i);
    }
    return out;
  };

  const tryDecodeBase64Xlsx = (buffer: ArrayBuffer) => {
    const text = new TextDecoder().decode(buffer).trim();
    if (!text) return null;
    if (!/^[A-Za-z0-9+/=\r\n]+$/.test(text)) return null;
    try {
      const bytes = decodeBase64ToBytes(text);
      return bytes[0] === 0x50 && bytes[1] === 0x4b ? bytes : null;
    } catch {
      return null;
    }
  };

  const ensureXlsxExtension = (name: string) => {
    if (/\.xlsx$/i.test(name)) return name;
    return `${name.replace(/\.[^/.]+$/, "")}.xlsx`;
  };

  const ensureCsvExtension = (name: string) => {
    if (/\.csv$/i.test(name)) return name;
    return `${name.replace(/\.[^/.]+$/, "")}.csv`;
  };

  const xlsxLooksValid = (buffer: ArrayBuffer) => {
    const bytes = new Uint8Array(buffer);
    if (bytes.length < 4) return false;
    const sig0 = 0x50;
    const sig1 = 0x4b;
    const sig2 = 0x05;
    const sig3 = 0x06;
    const minIndex = Math.max(0, bytes.length - 0xffff - 22);
    for (let i = bytes.length - 4; i >= minIndex; i -= 1) {
      if (
        bytes[i] === sig0 &&
        bytes[i + 1] === sig1 &&
        bytes[i + 2] === sig2 &&
        bytes[i + 3] === sig3
      ) {
        return true;
      }
    }
    return false;
  };

  const looksLikeCsv = (text: string) => {
    const sample = text.slice(0, 2048);
    if (!sample.includes("\n")) return false;
    if (/<\/?[a-z][\s>]/i.test(sample)) return false;
    const lines = sample.split("\n").slice(0, 5).filter(Boolean);
    return lines.length > 1 && lines.some((line) => line.includes(","));
  };

  const tryExtractErrorMessage = (text: string) => {
    const trimmed = text.trim();
    if (!trimmed) return null;
    if (trimmed.startsWith("{")) {
      try {
        const parsed = JSON.parse(trimmed);
        return parsed?.message || parsed?.error || null;
      } catch {
        return null;
      }
    }
    if (/<html/i.test(trimmed)) return "Download failed: server error page";
    return null;
  };

  const buildDownloadPayload = (buffer: ArrayBuffer, contentType: string) => {
    if (bufferLooksLikeXlsx(buffer)) {
      if (!xlsxLooksValid(buffer)) {
        throw new Error("Downloaded XLSX appears corrupted");
      }
      return {
        blob: new Blob([buffer], {
          type:
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
        extension: "xlsx",
      };
    }

    const decoded = tryDecodeBase64Xlsx(buffer);
    if (decoded) {
      return {
        blob: new Blob([decoded], {
          type:
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
        extension: "xlsx",
      };
    }

    const text = new TextDecoder().decode(buffer);
    const errMsg = tryExtractErrorMessage(text);
    if (errMsg) throw new Error(errMsg);

    if (
      contentType.includes("text/csv") ||
      contentType.includes("application/csv") ||
      looksLikeCsv(text)
    ) {
      return {
        blob: new Blob([text], { type: "text/csv" }),
        extension: "csv",
      };
    }

    throw new Error("Download failed: invalid file");
  };

  const getFilenameFromDisposition = (
    res: Response,
    fallbackName: string
  ) => {
    const header = res.headers.get("content-disposition");
    if (!header) return fallbackName;
    const match =
      /filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i.exec(header);
    if (!match?.[1]) return fallbackName;
    try {
      return decodeURIComponent(match[1]);
    } catch {
      return match[1];
    }
  };

  const handleDownload = async (
    apiFn: (token: string) => Promise<Response>,
    fallbackName: string
  ) => {
    if (downloading) return;
    setDownloading(true);
    setHasError(false);
    try {
      const res = await apiFn(token!);
      if (!res.ok) throw new Error("Download failed");
      const buffer = await res.arrayBuffer();
      const contentType = res.headers.get("content-type") || "";
      const { blob, extension } = buildDownloadPayload(buffer, contentType);
      const baseName = getFilenameFromDisposition(res, fallbackName);
      const filename =
        extension === "csv"
          ? ensureCsvExtension(baseName)
          : ensureXlsxExtension(baseName);
      triggerDownload(blob, filename);
    } catch (err) {
      setHasError(true);
      const message = err instanceof Error ? err.message : null;
      setStatusMessage(message || "Error during download.");
    } finally {
      setDownloading(false);
    }
  };

  const handleTransactionDownload = async () => {
    if (!transactionEventId.trim()) {
      setStatusMessage("Enter an event ID to download the report.");
      return;
    }
    if (downloading) return;
    setDownloading(true);
    setHasError(false);
    try {
      const res = await downloadTransactionReport(
        transactionEventId.trim(),
        token!
      );
      if (!res.ok) throw new Error("Transaction download failed");
      const buffer = await res.arrayBuffer();
      const contentType = res.headers.get("content-type") || "";
      const { blob, extension } = buildDownloadPayload(buffer, contentType);
      const baseName = getFilenameFromDisposition(
        res,
        `TransactionReport-${transactionEventId.trim()}`
      );
      const filename =
        extension === "csv"
          ? ensureCsvExtension(baseName)
          : ensureXlsxExtension(baseName);
      triggerDownload(blob, filename);
    } catch (err) {
      setHasError(true);
      const message = err instanceof Error ? err.message : null;
      setStatusMessage(message || "Error downloading transaction report.");
    } finally {
      setDownloading(false);
    }
  };

  const runImport = async (previewOnly: boolean) => {
    if (!selectedFile || busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setHasError(false);
    setStatusMessage(previewOnly ? "Checking your spreadsheet…" : "Importing inventory…");
    try {
      const action = mode === "update" ? updateInventoryExcel : uploadInventoryExcel;
      const summary = await action(selectedFile, token!, previewOnly);
      setPreview(previewOnly ? summary : null);
      setStatusMessage(summary.message);
      if (!previewOnly) {
        setSelectedFile(null);
        const input = document.getElementById("fileInput") as HTMLInputElement | null;
        if (input) input.value = "";
      }
    } catch (err: any) {
      setPreview(null);
      setHasError(true);
      setStatusMessage(err.response?.data?.message || err.message || "Import failed. Please try again.");
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  return (
    <>
      <PageBackground>
        <PageWrapper>
          <PageHeader>
            <BackLink href="/tools"><FiArrowLeft /> Tools</BackLink>
            <h1>Import inventory</h1>
            <p>Add items or update your catalog from a spreadsheet.</p>
          </PageHeader>

          <Section>
            <SectionHeading><span>01</span><h2>Prepare your spreadsheet</h2></SectionHeading>
            <ButtonGroup>
              <PrimaryButton
                disabled={downloading}
                onClick={() =>
                  handleDownload(
                    downloadInventorySample,
                    "InventorySample.xlsx"
                  )
                }
              >
                <FiDownload /> Sample Excel
              </PrimaryButton>
              <SecondaryButton
                disabled={downloading}
                onClick={() =>
                  handleDownload(
                    downloadCurrentInventory,
                    "CurrentInventory.xlsx"
                  )
                }
              >
                <FiDownload /> Current Inventory
              </SecondaryButton>
            </ButtonGroup>
            <TemplateExample>
              <div><FiFileText /><strong>Sample row</strong></div>
              <ExampleTable><thead><tr><th>SKU</th><th>Item Name</th><th>Category</th><th>Cost Per Unit</th></tr></thead>
                <tbody><tr><td>SAM-001</td><td>Sample item</td><td>SAMPLE</td><td>2.50</td></tr></tbody>
              </ExampleTable>
            </TemplateExample>
            <HelperText>New items need an item name and quantity. Leave SKU blank to generate one from the category, or keep the original SKU to update an item.</HelperText>
            <ImportNotes>
              <summary>Spreadsheet details</summary>
              <p>Replace the sample row, including SAMPLE, with your own item and a category from the workbook. Without a category, generated SKUs start with GEN.</p>
              <p>Each row with a blank SKU creates a new item, even if imported before. Repeat a supplied SKU to group variant rows.</p>
              <p>Quantities replace the Main inventory count. Other pools and existing photos stay unchanged. Blank optional cells preserve existing values.</p>
              <p>Square users: upload your Square catalog export (Items → Actions → Export Library) as-is. Item names, SKUs, prices, costs, categories, descriptions, options, and the default location's quantity are imported; new categories are created for you, and archived items become discontinued.</p>
            </ImportNotes>
          </Section>

          <ReportSection>
            <summary><FiDownload /> Transaction reports</summary>
            <TransactionRow>
              <DateFilterGroup>
                <DateField>
                  <label htmlFor="eventStartDate">From</label>
                  <DateInput
                    id="eventStartDate"
                    type="date"
                    value={eventStartDate}
                  onChange={(e) => { setEventStartDate(e.target.value); setTransactionEventId(""); }}
                  />
                </DateField>
                <DateField>
                  <label htmlFor="eventEndDate">To</label>
                  <DateInput
                    id="eventEndDate"
                    type="date"
                    value={eventEndDate}
                    onChange={(e) => { setEventEndDate(e.target.value); setTransactionEventId(""); }}
                  />
                </DateField>
              </DateFilterGroup>
              <EventSelectWrapper>
                <label htmlFor="eventSelect">Event</label>
                <EventSelect
                  id="eventSelect"
                  value={transactionEventId}
                  onChange={(e) => setTransactionEventId(e.target.value)}
                >
                  <option value="">
                    {eventsLoading
                      ? "Loading events..."
                      : "Select an event"}
                  </option>
                  {sortedEvents.map((event) => (
                    <option key={event._id} value={event._id}>
                      {formatEventOption(event)}
                    </option>
                  ))}
                </EventSelect>
                {eventsError && <InlineHint>{eventsError}</InlineHint>}
                {!eventsLoading &&
                  !eventsError &&
                  sortedEvents.length === 0 && (
                    <InlineHint>No events in this date range.</InlineHint>
                  )}
              </EventSelectWrapper>
              <TransactionButton>
                <SecondaryButton disabled={downloading || !transactionEventId || eventsLoading} onClick={handleTransactionDownload}>
                  <FiDownload /> Transaction Report
                </SecondaryButton>
              </TransactionButton>
            </TransactionRow>
          </ReportSection>

          <Section>
            <SectionHeading><span>02</span><h2>Upload & review</h2></SectionHeading>
            <ModeField><label htmlFor="importMode">What would you like to do?</label>
            <EventSelect id="importMode" disabled={busy} value={mode} onChange={e => {
              setMode(e.target.value as "add" | "update"); setPreview(null); setStatusMessage("");
            }}>
              <option value="add">Add new items</option>
              <option value="update">Update & add items</option>
            </EventSelect>
            <HelperText>{mode === "add" ? "Items with existing SKUs will be skipped." : "Existing SKUs will be updated. Other rows become new items."}</HelperText>
            </ModeField>
            <UploadSection>
              <DropZone
                htmlFor="fileInput"
                onDragOver={(e) => e.preventDefault()}
                onDrop={handleDrop}
              >
                {selectedFile ? <FiFileText size={28} /> : <FiUpload size={28} />}
                <p>
                  {selectedFile
                    ? selectedFile.name
                    : "Choose a spreadsheet or drop it here"}
                </p>
                <span>{selectedFile ? `${(selectedFile.size / 1024).toFixed(1)} KB · Click to replace` : "XLSX, XLS or CSV · Up to 10 MB · 5,000 items"}</span>
                <input
                  type="file"
                  id="fileInput"
                  accept=".xlsx,.xls,.csv"
                  disabled={busy}
                  aria-label="Inventory spreadsheet"
                  onChange={handleFileChange}
                  onClick={e => { e.currentTarget.value = ""; }}
                />
              </DropZone>
              <ButtonGroup>
                {selectedFile && <SecondaryButton aria-label="Remove selected file" title="Remove selected file" disabled={busy} onClick={() => {
                  setSelectedFile(null); setPreview(null); setStatusMessage(""); setHasError(false);
                  const input = document.getElementById("fileInput") as HTMLInputElement | null;
                  if (input) input.value = "";
                }}><FiX /></SecondaryButton>}
                <PrimaryButton disabled={!selectedFile || busy} onClick={() => runImport(true)}>
                  <FiUpload /> {busy ? "Please wait…" : "Review file"}
                </PrimaryButton>
                {preview && <SecondaryButton disabled={busy} onClick={() => runImport(false)}>
                  <FiFilePlus /> Confirm import
                </SecondaryButton>}
              </ButtonGroup>
            </UploadSection>

            {preview && <PreviewSummary role="status">
              <h3><FiCheckCircle /> Ready to import</h3>
              <dl><div><dt>New items</dt><dd>{preview.createdCount}</dd></div><div><dt>Updates</dt><dd>{preview.updatedCount}</dd></div><div><dt>Skipped</dt><dd>{preview.skippedCount}</dd></div></dl>
              {!!preview.generatedSkuCount && <p>{preview.generatedSkuCount} new items will receive an automatic SKU. Download Current Inventory after importing to keep these SKUs for future updates.</p>}
            </PreviewSummary>}
            {statusMessage && <Status role={hasError ? "alert" : "status"} style={{ color: hasError ? "#b91c1c" : undefined }}>{statusMessage}</Status>}
          </Section>
        </PageWrapper>
        <FooterWrapper><Footer /></FooterWrapper>
      </PageBackground>
    </>
  );
};

export default InventoryImportPage;

const PageBackground = styled.div`
  background: #fff;
  color: #20252b;
  min-height: 100vh;
  padding: 3rem 24px 0;
  letter-spacing: 0;
  @media (max-width: 600px) { padding: 24px 16px 0; }
  button, input, select { font: inherit; }
  button:focus-visible, input:focus-visible, select:focus-visible, summary:focus-visible, a:focus-visible { outline: 2px solid #2563eb; outline-offset: 3px; }
`;

const PageWrapper = styled.div`
  max-width: 880px;
  margin: 0 auto;
  width: 100%;
`;

const PageHeader = styled.header`
  padding-bottom: 32px;
  border-bottom: 1px solid #e2e5e8;
  h1 { font-size: 30px; line-height: 1.2; font-weight: 650; margin: 20px 0 10px; }
  p { color: #626b73; font-size: 15px; }
  @media (max-width: 600px) { padding-bottom: 20px; h1 { font-size: 24px; } }
`;
const BackLink = styled.a`
  display: inline-flex; align-items: center; gap: 8px; color: #626b73; font-size: 13px;
  @media (max-width: 600px) { min-height: 44px; }
  &:hover { color: #2563eb; }
`;
const SectionHeading = styled.div`
  display: flex; align-items: center; gap: 12px; margin-bottom: 20px;
  @media (max-width: 600px) { margin-bottom: 16px; }
  span { color: #76808b; font-size: 12px; font-weight: 600; }
  h2 { font-size: 18px; font-weight: 600; }
`;
const TemplateExample = styled.div`
  margin: 24px 0 16px; overflow-x: auto;
  > div { display: flex; align-items: center; gap: 8px; font-size: 12px; color: #626b73; margin-bottom: 10px; }
`;
const ExampleTable = styled.table`
  width: 100%; border-collapse: collapse; text-align: left; font-size: 13px;
  th { background: #f5f6f7; font-weight: 500; color: #626b73; }
  th, td { padding: 10px 12px; border-bottom: 1px solid #e2e5e8; white-space: nowrap; }
  td:first-child { font-family: monospace; color: #24705a; }
`;
const ImportNotes = styled.details`
  font-size: 13px; color: #626b73; line-height: 1.6;
  summary { cursor: pointer; color: #343d46; width: fit-content; padding: 8px 0; }
  p { margin-top: 10px; max-width: 720px; }
`;
const ReportSection = styled.details`
  border-bottom: 1px solid #e2e5e8; padding: 18px 0;
  summary { cursor: pointer; font-size: 14px; font-weight: 500; padding: 6px 0; }
  summary svg { display: inline; vertical-align: middle; margin: 0 6px; }
  &[open] { padding-bottom: 24px; }
`;
const ModeField = styled.div`
  display: flex; flex-direction: column; gap: 8px; margin-bottom: 20px; max-width: 440px;
  label { font-size: 13px; font-weight: 500; }
`;
const PreviewSummary = styled.div`
  margin-top: 24px; padding: 20px 0; border-top: 1px solid #e2e5e8;
  h3 { display: flex; align-items: center; gap: 8px; color: #24705a !important; }
  dl { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; margin: 16px 0; }
  @media (max-width: 600px) { dl { gap: 8px; } dd { font-size: 20px; } }
  dt { font-size: 13px; color: #626b73; } dd { font-size: 24px; font-weight: 600; margin: 0; }
  p { font-size: 13px; line-height: 1.6; color: #626b73; }
`;
const FooterWrapper = styled.div`
  max-width: 880px; margin: 0 auto;
`;

const Section = styled.section`
  padding: 28px 0;
  border-bottom: 1px solid #e2e5e8;
  &:last-child { border-bottom: 0; }
  @media (max-width: 600px) { padding: 22px 0; }
  h3 {
    margin-bottom: 0.75rem;
    font-weight: 600;
    color: #1f2937;
  }
`;

const HelperText = styled.p`
  margin: 0 0 0.75rem;
  font-size: 13px;
  color: #4b5563;
  line-height: 1.6;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  @media (max-width: 600px) { > button { flex: 1 1 100%; } > button[aria-label="Remove selected file"] { flex: 0 0 auto; } }
`;

const UploadSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const DropZone = styled.label`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  min-height: 190px;
  @media (max-width: 600px) { min-height: 150px; padding: 1.25rem 1rem; }
  min-width: 0;
  flex: 1;
  border: 1px dashed #aab6c2;
  padding: 1.5rem;
  border-radius: 6px;
  text-align: center;
  color: #4b5563;
  background-color: #f9fafb;
  transition: background-color 0.3s ease;
  cursor: pointer;
  input { position: absolute; inset: 0; opacity: 0; width: 100%; height: 100%; cursor: pointer; }
  &:focus-within { outline: 2px solid #2563eb; outline-offset: 3px; }
  span { font-size: 12px; color: #626b73; }
  > svg { color: #386cd0; }

  &:hover {
    background-color: #f3f4f6;
  }

  p {
    font-size: 14px;
    margin: 0;
    overflow-wrap: anywhere;
    max-width: 100%;
    font-weight: 500;
  }
`;

const PrimaryButton = styled.button<{ disabled?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background-color: #2563eb;
  color: white;
  padding: 10px 16px;
  min-height: 42px;
  justify-content: center;
  font-weight: 500;
  font-size: 14px;
  @media (max-width: 600px) { min-height: 46px; }
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: background-color 0.2s ease;

  svg {
    height: 16px;
    width: 16px;
  }

  &:hover:enabled {
    background-color: #2563eb;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SecondaryButton = styled(PrimaryButton)`
  background-color: #fff;
  color: #343d46;
  border: 1px solid #d5dbe1;

  &:hover:enabled {
    background-color: #f3f5f7;
  }
`;

const Status = styled.div`
  margin-top: 1rem;
  font-weight: 500;
  color: #047857;
`;

const TransactionRow = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-top: 0.75rem;
  align-items: flex-start;
`;

const TransactionButton = styled.div`
  align-self: flex-end;
  @media (max-width: 600px) { align-self: stretch; width: 100%; > button { width: 100%; } }
`;

const DateFilterGroup = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  @media (max-width: 600px) { width: 100%; > div { flex: 1; min-width: 0; } }
`;

const DateField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  font-size: 12px;
  color: #6b7280;
`;

const DateInput = styled.input`
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 0.55rem 0.75rem;
  font-size: 14px;
  min-width: 0;
  width: 100%;
  min-height: 42px;
  @media (max-width: 600px) { font-size: 16px; min-height: 46px; }
`;

const EventSelectWrapper = styled.div`
  @media (max-width: 600px) { min-height: 0; flex: 1 1 100%; }
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  flex: 1;
  min-width: min(220px, 100%);
  font-size: 12px;
  color: #6b7280;
  min-height: 68px;
`;

const EventSelect = styled.select`
  width: 100%;
  min-width: 0;
  min-height: 42px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 0.55rem 0.75rem;
  font-size: 14px;
  color: #111827;
  background: #fff;
  @media (max-width: 600px) { font-size: 16px; min-height: 46px; }
`;

const InlineHint = styled.span`
  font-size: 12px;
  color: #9ca3af;
  min-height: 16px;
`;
